import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import { TIME_SIGNATURES } from '../config/timeSignatures';
import { analyzeInstrumentIds } from '../utils/smartInstrumentId';
import { devLog } from '../utils/devLog';
import AsciiImportButton from './AsciiImportButton';
import AsciiExportButton from './AsciiExportButton';
import type { AsciiOutputProps, TimeSignature } from '../types';

function getBarDivisions(timeSignature: TimeSignature, stepsPerBar: number): number {
  const { beats } = TIME_SIGNATURES[timeSignature];
  const stepsPerBeat = stepsPerBar / beats;
  return stepsPerBeat;
}

export default function AsciiOutput({
  instruments,
  grid,
  stepsPerBar,
  timeSignature,
  noteDivision,
  tempo,
  onImport,
  currentInstruments,
  disabled,
  onError
}: AsciiOutputProps): JSX.Element {
  const { t } = useLanguage();
  const { getClass } = useThemeContext();
  const getThemeClass = getClass;
  const [debugMode, setDebugMode] = useState<boolean>(false);

  const generateAscii = (): string => {
    return instruments
      .map(({ id }) => {
        if (!grid[id]) return `${id}: ${''.padEnd(stepsPerBar, '-')}`;
        const line = grid[id].slice(0, stepsPerBar).map((v, i) => {
          const mark = v || '-';
          return (i % getBarDivisions(timeSignature, stepsPerBar) === 0 && i !== 0 ? '|' : '') + mark;
        });
        return `${id}: ${line.join('')}`;
      })
      .join('\n');
  };

  const generateDebugInfo = (): JSX.Element | null => {
    if (!debugMode) return null;
    
    const instrumentIds = instruments.map(inst => inst.id);
    const analysisResults = analyzeInstrumentIds(instrumentIds, true);
    
    devLog('🔍 [ASCII Debug] Analysis triggered for IDs:', instrumentIds);
    
    return (
      <div className={`mt-3 p-3 rounded border ${getThemeClass('border')} ${getThemeClass('input')}`}>
        <h4 className="font-semibold mb-2">🔍 Debug Info - Smart ID Analysis</h4>
        <div className="space-y-2 text-sm font-mono">
          {analysisResults.map(result => (
            <div key={result.id} className="flex flex-wrap gap-2 items-center">
              <span className="font-bold text-blue-600">{result.id}:</span>
              <span className="text-green-600">{result.type}</span>
              <span className="text-gray-600">({result.name})</span>
              <span className={`px-2 py-1 rounded text-xs ${
                result.matchType === 'exact' ? 'bg-green-100 text-green-800' :
                result.matchType === 'pattern' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {result.matchType}
              </span>
              <span className="text-gray-500 text-xs">{result.sampleId}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <strong>Legend:</strong> 
          <span className="ml-2 px-1 bg-green-100 text-green-800 rounded">exact</span> = 精確匹配
          <span className="ml-2 px-1 bg-yellow-100 text-yellow-800 rounded">pattern</span> = 模式匹配
          <span className="ml-2 px-1 bg-red-100 text-red-800 rounded">fallback</span> = 默認值
        </div>
      </div>
    );
  };

  return (
    <div className={`p-3 rounded ${getThemeClass('grid')} ${getThemeClass('border')}`}>
      <div className="flex flex-wrap items-center mb-2 gap-2">
        <h2 className="text-xl font-bold">📝 {t('asciiOutput')}</h2>
        <AsciiImportButton
          onImport={onImport}
          currentInstruments={currentInstruments}
          disabled={disabled}
          onError={onError}
        />
        <AsciiExportButton
          instruments={instruments}
          grid={grid}
          stepsPerBar={stepsPerBar}
          timeSignature={timeSignature}
          noteDivision={noteDivision}
          tempo={tempo}
          disabled={disabled}
        />
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => setDebugMode(!debugMode)}
            className={`px-2 py-1 text-sm rounded border transition-colors ${
              debugMode ? getThemeClass('buttonWarning') : getThemeClass('buttonSecondary')
            }`}
            title={t('asciiDebugToggle')}
          >
            <span>🔍</span>
            <span className="hidden lg:inline ml-1">{t('debugMode')}</span>
          </button>
        )}
      </div>
      <pre className={`p-2 rounded border font-mono whitespace-pre-wrap ${getThemeClass('border')} ${getThemeClass('input')}`}>
        {generateAscii()}
      </pre>
      {generateDebugInfo()}
    </div>
  );
} 